import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Mail, MessageSquare, Plus, Send, Trash2, Eye, X, FileText, AlertTriangle } from "lucide-react";
import DOMPurify from "dompurify";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CampaignBuilder() {
  const [tab, setTab] = useState("email");
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [smsCampaigns, setSmsCampaigns] = useState([]);
  const [templates, setTemplates] = useState({ default: [], custom: [] });
  const [smsTemplates, setSmsTemplates] = useState([]);
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [segments, setSegments] = useState({});

  const [emailForm, setEmailForm] = useState({
    name: "", subject: "", from_label: "Fit Print Bros", from_email: "orders@fitprintbros.com",
    reply_to: "mike@fitprintbros.com", html_content: "", audience: "all_clients", custom_emails: [],
  });

  const [smsForm, setSmsForm] = useState({
    name: "", message: "", audience: "all_clients", custom_phones: [],
  });

  const fetchAll = useCallback(async () => {
    try {
      const [ec, sc, et, st, segRes] = await Promise.all([
        axios.get(`${API}/campaigns/email`, { withCredentials: true }),
        axios.get(`${API}/campaigns/sms`, { withCredentials: true }),
        axios.get(`${API}/campaigns/email/templates`, { withCredentials: true }),
        axios.get(`${API}/campaigns/sms/templates`, { withCredentials: true }),
        axios.get(`${API}/crm/segments`, { withCredentials: true }).catch(() => ({ data: { definitions: {} } })),
      ]);
      setEmailCampaigns(ec.data.campaigns);
      setSmsCampaigns(sc.data.campaigns);
      setTemplates(et.data);
      setSmsTemplates(st.data.templates);
      setSegments(segRes.data?.definitions || {});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const loadTemplate = (template) => {
    setEmailForm(f => ({
      ...f,
      subject: template.subject || f.subject,
      html_content: template.html_content,
      name: f.name || template.name,
    }));
  };

  const saveEmailCampaign = async () => {
    try {
      await axios.post(`${API}/campaigns/email`, emailForm, { withCredentials: true });
      setEditing(null);
      setEmailForm({ name: "", subject: "", from_label: "Fit Print Bros", from_email: "orders@fitprintbros.com", reply_to: "mike@fitprintbros.com", html_content: "", audience: "all_clients", custom_emails: [] });
      fetchAll();
    } catch (err) { alert("Error saving campaign"); }
  };

  const sendEmailCampaign = async (id) => {
    if (!window.confirm("Send this campaign now? This action cannot be undone.")) return;
    try {
      const { data } = await axios.post(`${API}/campaigns/email/${id}/send`, {}, { withCredentials: true });
      alert(data.message);
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Send failed"); }
  };

  const saveSMSCampaign = async () => {
    try {
      await axios.post(`${API}/campaigns/sms`, smsForm, { withCredentials: true });
      setEditing(null);
      setSmsForm({ name: "", message: "", audience: "all_clients", custom_phones: [] });
      fetchAll();
    } catch (err) { alert("Error saving campaign"); }
  };

  const sendSMSCampaign = async (id) => {
    if (!window.confirm("Queue this SMS campaign?")) return;
    try {
      const { data } = await axios.post(`${API}/campaigns/sms/${id}/send`, {}, { withCredentials: true });
      alert(data.message + (data.note ? `\n\n${data.note}` : ""));
      fetchAll();
    } catch (err) { alert(err.response?.data?.detail || "Send failed"); }
  };

  const deleteCampaign = async (type, id) => {
    if (!window.confirm("Delete this campaign?")) return;
    await axios.delete(`${API}/campaigns/${type}/${id}`, { withCredentials: true });
    fetchAll();
  };

  /* ═══ EMAIL EDITOR ═══ */
  if (editing === "email") {
    return (
      <div className="space-y-4" data-testid="email-editor">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-white">New Email Campaign</h2>
          <Button variant="ghost" onClick={() => setEditing(null)} className="text-zinc-400 shrink-0"><X className="w-5 h-5" /></Button>
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Start from a template:</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {[...templates.default, ...templates.custom].map(t => (
              <Button key={t.id} variant="outline" size="sm" onClick={() => loadTemplate(t)} className="border-zinc-700 text-zinc-300 hover:text-orange-400 hover:border-orange-400">
                <FileText className="w-3 h-3 mr-1" /> {t.name}
              </Button>
            ))}
          </div>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-zinc-400 text-xs">Campaign Name *</Label><Input value={emailForm.name} onChange={e => setEmailForm({...emailForm, name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" placeholder="Q1 Newsletter" data-testid="campaign-name" /></div>
              <div><Label className="text-zinc-400 text-xs">Subject Line *</Label><Input value={emailForm.subject} onChange={e => setEmailForm({...emailForm, subject: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" placeholder="Your subject line" data-testid="campaign-subject" /></div>
              <div><Label className="text-zinc-400 text-xs">From Name</Label><Input value={emailForm.from_label} onChange={e => setEmailForm({...emailForm, from_label: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" /></div>
              <div><Label className="text-zinc-400 text-xs">From Email</Label>
                <Select value={emailForm.from_email} onValueChange={v => setEmailForm({...emailForm, from_email: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="orders@fitprintbros.com">orders@fitprintbros.com</SelectItem>
                    <SelectItem value="quote@fitprintbros.com">quote@fitprintbros.com</SelectItem>
                    <SelectItem value="support@fitprintbros.com">support@fitprintbros.com</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-400 text-xs">Audience</Label>
                <Select value={emailForm.audience} onValueChange={v => setEmailForm({...emailForm, audience: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_clients">All Clients</SelectItem>
                    <SelectItem value="all_leads">All Leads</SelectItem>
                    <SelectItem value="everyone">Everyone (Clients + Leads)</SelectItem>
                    {Object.entries(segments).map(([id, def]) => (
                      <SelectItem key={id} value={`segment:${id}`}>Segment: {def.label}</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom List</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {emailForm.audience === "custom" && (
              <div><Label className="text-zinc-400 text-xs">Custom Emails (comma-separated)</Label>
                <Textarea value={emailForm.custom_emails.join(", ")} onChange={e => setEmailForm({...emailForm, custom_emails: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} className="bg-zinc-800 border-zinc-700 text-white mt-1" rows={2} placeholder="email1@example.com, email2@example.com" /></div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-zinc-400 text-xs">Email HTML Content *</Label>
                <Button variant="ghost" size="sm" onClick={() => setPreview(emailForm.html_content)} className="text-orange-400 text-xs"><Eye className="w-3 h-3 mr-1" /> Preview</Button>
              </div>
              <Textarea value={emailForm.html_content} onChange={e => setEmailForm({...emailForm, html_content: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1 font-mono text-xs" rows={16} placeholder="<div>Your email HTML...</div>" data-testid="email-html" />
              <p className="text-zinc-600 text-xs mt-1">Variables: {"{{name}}"}, {"{{site_url}}"}, {"{{offer_text}}"}, {"{{cta_url}}"}, {"{{cta_text}}"}</p>
            </div>

            <Button onClick={saveEmailCampaign} disabled={!emailForm.name || !emailForm.subject || !emailForm.html_content} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="save-campaign-btn">
              Save as Draft
            </Button>
          </CardContent>
        </Card>

        {preview && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setPreview(null)}>
            <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
                <span className="font-medium text-gray-900">Email Preview</span>
                <Button variant="ghost" size="sm" onClick={() => setPreview(null)}><X className="w-4 h-4" /></Button>
              </div>
              <div className="p-4" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.replace(/\{\{name\}\}/g, 'John').replace(/\{\{site_url\}\}/g, '#'), { ALLOWED_TAGS: ['div','p','span','a','h1','h2','h3','h4','strong','em','br','img','table','tr','td','th','thead','tbody','ul','ol','li','hr','blockquote'], ALLOWED_ATTR: ['href','src','alt','style','class','target','rel','width','height'] }) }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  /* ═══ SMS EDITOR ═══ */
  if (editing === "sms") {
    const charCount = smsForm.message.length;
    const segments = Math.max(1, Math.ceil(charCount / 160));
    const hasStop = smsForm.message.toUpperCase().includes("STOP");

    return (
      <div className="space-y-4" data-testid="sms-editor">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-xl sm:text-2xl font-bold text-white">New SMS Campaign</h2>
          <Button variant="ghost" onClick={() => setEditing(null)} className="text-zinc-400 shrink-0"><X className="w-5 h-5" /></Button>
        </div>

        <div>
          <Label className="text-zinc-400 text-sm">Start from a template:</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {smsTemplates.map(t => (
              <Button key={t.id} variant="outline" size="sm" onClick={() => setSmsForm(f => ({ ...f, message: t.message, name: f.name || t.name }))} className="border-zinc-700 text-zinc-300 hover:text-orange-400">
                <MessageSquare className="w-3 h-3 mr-1" /> {t.name}
              </Button>
            ))}
          </div>
        </div>

        <Card className="bg-zinc-900 border-zinc-800">
          <CardContent className="p-4 sm:p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="text-zinc-400 text-xs">Campaign Name *</Label><Input value={smsForm.name} onChange={e => setSmsForm({...smsForm, name: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" placeholder="Order Update SMS" data-testid="sms-name" /></div>
              <div><Label className="text-zinc-400 text-xs">Audience</Label>
                <Select value={smsForm.audience} onValueChange={v => setSmsForm({...smsForm, audience: v})}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all_clients">All Clients (with phone)</SelectItem>
                    <SelectItem value="all_leads">All Leads (with phone)</SelectItem>
                    {Object.entries(segments).map(([id, def]) => (
                      <SelectItem key={id} value={`segment:${id}`}>Segment: {def.label}</SelectItem>
                    ))}
                    <SelectItem value="custom">Custom List</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {smsForm.audience === "custom" && (
              <div><Label className="text-zinc-400 text-xs">Phone Numbers (comma-separated)</Label>
                <Textarea value={smsForm.custom_phones.join(", ")} onChange={e => setSmsForm({...smsForm, custom_phones: e.target.value.split(",").map(s => s.trim()).filter(Boolean)})} className="bg-zinc-800 border-zinc-700 text-white mt-1" rows={2} placeholder="+15551234567, +15559876543" /></div>
            )}

            <div>
              <Label className="text-zinc-400 text-xs">Message *</Label>
              <Textarea value={smsForm.message} onChange={e => setSmsForm({...smsForm, message: e.target.value})} className="bg-zinc-800 border-zinc-700 text-white mt-1" rows={4} placeholder="Fit Print Bros: Your order is ready! ..." data-testid="sms-message" />
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-xs">
                <span className={`${charCount > 160 ? 'text-yellow-400' : 'text-zinc-500'}`}>{charCount}/160 chars</span>
                <span className="text-zinc-500">{segments} SMS segment{segments > 1 ? 's' : ''}</span>
                {!hasStop && <span className="text-red-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Must include "Reply STOP to opt out"</span>}
                {hasStop && <span className="text-green-400">TCPA compliant</span>}
              </div>
              <p className="text-zinc-600 text-xs mt-1">Variables: {"{{name}}"}, {"{{order_number}}"}, {"{{status}}"}, {"{{site_url}}"}, {"{{track_url}}"}</p>
            </div>

            <div className="bg-zinc-800 rounded-lg p-3">
              <p className="text-zinc-400 text-xs font-medium mb-2">TCPA/CTIA Compliance:</p>
              <ul className="text-zinc-500 text-xs space-y-1">
                <li>&#x2713; Include opt-out language ("Reply STOP to opt out")</li>
                <li>&#x2713; Identify business name in every message</li>
                <li>&#x2713; Send during business hours (8AM-9PM)</li>
                <li>&#x2713; Recipients must have prior consent</li>
              </ul>
            </div>

            <Button onClick={saveSMSCampaign} disabled={!smsForm.name || !smsForm.message || !hasStop} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="save-sms-btn">
              Save as Draft
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ═══ CAMPAIGN LIST ═══ */
  return (
    <div className="space-y-4" data-testid="campaign-builder">
      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-zinc-800 pb-2">
        <button onClick={() => setTab("email")} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 rounded-t-lg transition ${tab === 'email' ? 'bg-zinc-800 text-orange-400' : 'text-zinc-500 hover:text-white'}`}>
          <Mail className="w-4 h-4" /> Email
        </button>
        <button onClick={() => setTab("sms")} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 rounded-t-lg transition ${tab === 'sms' ? 'bg-zinc-800 text-orange-400' : 'text-zinc-500 hover:text-white'}`}>
          <MessageSquare className="w-4 h-4" /> SMS
        </button>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          {tab === "email" ? <Mail className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" /> : <MessageSquare className="w-5 h-5 sm:w-6 sm:h-6 text-orange-400" />}
          {tab === "email" ? "Email Campaigns" : "SMS Campaigns"}
        </h2>
        <Button onClick={() => setEditing(tab)} className="bg-gradient-to-r from-orange-500 to-red-600 text-white w-full sm:w-auto" data-testid="new-campaign-btn">
          <Plus className="w-4 h-4 mr-1" /> New {tab === "email" ? "Email" : "SMS"} Campaign
        </Button>
      </div>

      {/* List */}
      {loading ? <div className="text-zinc-500 text-center py-8">Loading...</div> : (
        <div className="space-y-2">
          {(tab === "email" ? emailCampaigns : smsCampaigns).length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800"><CardContent className="py-12 text-center">
              {tab === "email" ? <Mail className="w-10 h-10 text-zinc-700 mx-auto mb-3" /> : <MessageSquare className="w-10 h-10 text-zinc-700 mx-auto mb-3" />}
              <p className="text-zinc-400">No {tab} campaigns yet.</p>
              <p className="text-zinc-600 text-sm mt-1">Create your first campaign to start engaging your audience.</p>
            </CardContent></Card>
          ) : (tab === "email" ? emailCampaigns : smsCampaigns).map(campaign => (
            <Card key={campaign.id} className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${campaign.status === 'sent' ? 'bg-green-500/20' : 'bg-zinc-800'}`}>
                    {tab === "email" ? <Mail className={`w-5 h-5 ${campaign.status === 'sent' ? 'text-green-400' : 'text-zinc-500'}`} /> : <MessageSquare className={`w-5 h-5 ${campaign.status === 'sent' ? 'text-green-400' : 'text-zinc-500'}`} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{campaign.name}</p>
                    <p className="text-zinc-500 text-sm truncate">{tab === "email" ? campaign.subject : campaign.message?.substring(0, 60)}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={campaign.status === 'sent' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}>{campaign.status}</Badge>
                    {campaign.status === 'sent' && <span className="text-zinc-400 text-sm">{campaign.sent_count} sent</span>}
                    {campaign.status === 'draft' && (
                      <Button size="sm" onClick={() => tab === "email" ? sendEmailCampaign(campaign.id) : sendSMSCampaign(campaign.id)} className="bg-gradient-to-r from-orange-500 to-red-600 text-white" data-testid="send-campaign-btn">
                        <Send className="w-3 h-3 mr-1" /> Send
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => deleteCampaign(tab, campaign.id)} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
